import { Component, inject, OnInit, HostListener } from '@angular/core';
import { Router, RouterModule, RouterLink } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import {
  FormBuilder,
  ReactiveFormsModule,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { CommonModule } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { EnumService } from '@app/shared/components/api/services/enum.service';
import { forkJoin } from 'rxjs';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { PaginatorControlComponent } from '@app/shared/components/paginator-control/paginator-control.component';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { CardDoacaoComponent } from '@app/shared/components/card-doacao/card-doacao.component';
import { SkeletonLoaderComponent } from '@app/shared/components/skeleton-loader/skeleton-loader.component';
import { PagamentosService } from '@app/shared/components/api/services/pagamentos.service';
import { UserService } from '@app/auth/services/user.service';
import { Skeleton } from 'primeng/skeleton';
import { DetalhePagamentoComponent } from '@app/features-modules/admin/components/pagamentos/components/detalhe-pagamento/detalhe-pagamento.component';
import { Drawer } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { Menu } from 'primeng/menu';
import { MoneyPipe } from '@app/shared/pipes/money.pipe';

@Component({
  selector: 'app-dashboard-doacao',
  imports: [
    RouterModule,
    RouterLink,
    DatePickerModule,
    Select,
    ReactiveFormsModule,
    TagModule,
    CardDoacaoComponent,
    CommonModule,
    PaginatorControlComponent,
    DataVoidComponent,
    SkeletonLoaderComponent,
    Skeleton,
    DetalhePagamentoComponent,
    Drawer,
    DialogModule,
    Textarea,
    MoneyPipe,
  ],
  standalone: true,
  providers: [EnumService, PagamentosService, UserService],
  templateUrl: './dashboard-doacao.component.html',
  styleUrl: './dashboard-doacao.component.scss',
})
export class DashboardDoacaoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly pagamentosService = inject(PagamentosService);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);

  formFilters!: FormGroup;
  formReembolso!: FormGroup;
  showDialogReembolso: boolean = false;
  selectedPagamentoReembolso: any = null;
  loadingReembolso: boolean = false;

  motivosReembolso = [
    { label: 'Contribuição duplicada por engano', value: 'duplicado' },
    { label: 'Valor transferido incorreto', value: 'valor_incorreto' },
    { label: 'Campanha cancelada ou suspeita', value: 'campanha_problema' },
    { label: 'Problema no método de pagamento', value: 'problema_metodo' },
    { label: 'Outro motivo', value: 'outro' },
  ];

  metodoList: any[] = [];
  estadoList: any[] = [];
  pagination: Pagination<any> = new Pagination<any>();
  loading: boolean = false;
  indicadores: Record<string, number> = {};
  loadIndicators: boolean = false;
  userID: any = this.userService.getUserId();
  totalDoado: number = 0;
  totalProjetosApoiados: number = 0;
  totalApoios: number = 0;
  showDetalhesPagamento: boolean = false;
  pagamentoID: any;

  constructor() {}

  ngOnInit(): void {
    this.formFilters = this.fb.group({
      DataCriacao: [null],
      MetodoPagamentoENUM: [null],
      EstadoPagamentoENUM: [null],
    });

    this.formReembolso = this.fb.group({
      motivo: ['duplicado', Validators.required],
      descricao: ['', [Validators.required, Validators.minLength(10)]],
    });

    this.loadDropdowns();
    this.getAllpagamentos();

    //capturar qualquer alteração nos filtros e fazer a requisição novamente
    this.formFilters.valueChanges.subscribe(() => {
      this.getAllpagamentos();
    });
  }

  isConfirmado(pagamento: any): boolean {
    if (!pagamento) return false;

    const estadoStr = (pagamento?.estadoPagamento || pagamento?.estado || '')
      .toString()
      .trim()
      .toLowerCase();

    // Se estiver explicitamente cancelado, pendente, recusado, retornado ou reembolsado, não pode ter reembolso
    if (
      estadoStr.includes('cancel') ||
      estadoStr.includes('pend') ||
      estadoStr.includes('recus') ||
      estadoStr.includes('retorn') ||
      estadoStr.includes('reembols')
    ) {
      return false;
    }

    const enumVal = Number(
      pagamento?.estadoPagamentoENUM ??
        pagamento?.estadoPagamentoId ??
        pagamento?.estadoENUM,
    );
    if (!isNaN(enumVal) && enumVal > 0 && enumVal !== 2) {
      return false;
    }

    return (
      estadoStr === 'confirmado' ||
      estadoStr === 'pago' ||
      estadoStr === 'aprovado' ||
      enumVal === 2
    );
  }

  getActionsByEstado(campanha: any): MenuItem[] {
    const baseActions: MenuItem[] = [
      {
        label: 'Ver detalhes',
        icon: 'pi pi-eye',
        command: () => this.viewDetails(campanha),
      },
    ];

    // Reembolso visível exclusivamente para pagamentos com estado confirmado
    if (this.isConfirmado(campanha)) {
      baseActions.push({
        label: 'Reembolso',
        icon: 'pi pi-wallet',
        command: () => this.abrirReembolso(campanha),
      });
    }

    return baseActions;
  }

  abrirReembolso(pagamento: any) {
    if (!this.isConfirmado(pagamento)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Ação não permitida',
        detail: 'Apenas pagamentos confirmados podem solicitar reembolso.',
        life: 4000,
      });
      return;
    }

    this.selectedPagamentoReembolso = pagamento;
    this.formReembolso.reset({
      motivo: 'duplicado',
      descricao: '',
    });
    this.showDialogReembolso = true;
  }

  fecharDialogReembolso() {
    this.showDialogReembolso = false;
    this.selectedPagamentoReembolso = null;
  }

  submeterPedidoReembolso() {
    if (this.formReembolso.invalid) {
      this.formReembolso.markAllAsTouched();
      return;
    }

    this.loadingReembolso = true;
    const codApoio =
      this.selectedPagamentoReembolso?.apoioID ||
      this.selectedPagamentoReembolso?.id ||
      '';

    setTimeout(() => {
      this.loadingReembolso = false;
      this.showDialogReembolso = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Solicitação Registada',
        detail: `O seu pedido de reembolso para o apoio #${codApoio} foi enviado com sucesso. A nossa equipa de suporte entrará em contacto através do seu e-mail cadastrado.`,
        life: 6000,
      });
      this.selectedPagamentoReembolso = null;
    }, 600);
  }

  contactarSuporteReembolso() {
    const codApoio =
      this.selectedPagamentoReembolso?.apoioID ||
      this.selectedPagamentoReembolso?.id ||
      '';
    const campanhaTitulo =
      this.selectedPagamentoReembolso?.apoio?.campanha?.titulo || 'Campanha';
    const valor = this.selectedPagamentoReembolso?.apoio?.valorApoio || '';
    const subject = encodeURIComponent(
      `Solicitação de Reembolso - Apoio #${codApoio}`,
    );
    const body = encodeURIComponent(
      `Olá Equipa de Suporte FundCube,\n\n` +
        `Gostaria de solicitar assistência e análise de reembolso referente ao seguinte apoio:\n` +
        `- Código do Apoio: #${codApoio}\n` +
        `- Campanha: ${campanhaTitulo}\n` +
        `- Valor: ${valor} Kz\n\n` +
        `Motivo: ${this.formReembolso.get('motivo')?.value}\n` +
        `Descrição: ${this.formReembolso.get('descricao')?.value || 'N/A'}\n\n` +
        `Aguardando retorno!`,
    );
    window.open(
      `mailto:suporte@fundcube.ao?subject=${subject}&body=${body}`,
      '_blank',
    );
  }

  // Dropdowns
  loadDropdowns() {
    forkJoin({
      // Enums
      estadopagamento: this.enumService.getAllEstadoPagamento(),
      metodoPagamento: this.enumService.getAllMetodoPagamento(),
    }).subscribe((response) => {
      // Enums
      if (response.estadopagamento?.data) {
        this.estadoList = Object.entries(response.estadopagamento.data).map(
          ([key, value]) => ({
            id: Number(key),
            label: value as string,
          }),
        );
      }
      if (response.metodoPagamento?.data) {
        this.metodoList = Object.entries(response.metodoPagamento.data).map(
          ([key, value]) => ({
            id: Number(key),
            label: value as string,
          }),
        );
      }
    });
  }

  getAllpagamentos() {
    this.loading = true;
    this.getIndicators();
    // garantir que os valores null não são enviados na query
    const filters = { ...this.formFilters.value };
    Object.keys(filters).forEach((key) => {
      if (filters[key] === null) {
        delete filters[key];
      }
    });

    const params = {
      PageSize: 10000,
      PageNumber: 1,
      OrderBy: 'id desc',
      UtilizadorID: this.userID,
      ...filters,
    };
    this.pagamentosService.list(params).subscribe({
      next: (res) => {
        if (!res.data || res.data.length < 1) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Usuário sem doações',
            detail: 'Ainda não fizeste nenhuma doação.',
            life: 5000,
          });
        }

        this.pagination = new Pagination<any>().deserialize(res);

        // Cálculo do total doado
        const total = this.pagination.data?.reduce((acc, item) => {
          return acc + (item?.apoio?.valorApoio || 0);
        }, 0);
        this.totalDoado = total || 0;

        // Total de doacoes feitas
        this.totalApoios = this.pagination?.data?.length || 0;
        // Cálculo do total de projetos apoiados (Pelo ID da campanha)
        const uniqueProjetos = new Set();
        this.pagination.data?.forEach((item) => {
          if (item?.apoio?.campanha?.id) {
            uniqueProjetos.add(item.apoio.campanha.id);
          }
        });
        this.totalProjetosApoiados = uniqueProjetos.size;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      },
    });
  }

  getIndicators() {
    this.loadIndicators = true;
    const filters = { ...this.formFilters.value };
    Object.keys(filters).forEach((key) => {
      if (filters[key] === null) {
        delete filters[key];
      }
    });

    const params = {
      PageSize: 1000000,
      PageNumber: this.pagination.pageNumber || 1,
      OrderBy: 'id desc',
      UtilizadorID: this.userID,
      ...filters,
    };

    this.pagamentosService.list(params).subscribe({
      next: (res) => {
        if (!res.data || res.data.length < 1) {
          // this.messageService.add({
          //   severity: 'warn',
          //   summary: 'Usuário sem campanhas',
          //   detail: 'Ainda não criaste nenhuma campanha.'
          // });
          this.loadIndicators = false;
          return;
        }
        const campanhas = res?.data;
        // Mapeamento dos estados
        const indicadores: Record<string, number> = {};

        campanhas?.forEach((c: { estadoCampanha: string }) => {
          const estado = c.estadoCampanha || 'Desconhecido';
          indicadores[estado] = (indicadores[estado] || 0) + 1;
        });

        // indicadores
        this.indicadores = {
          ...indicadores,
        };
        this.loadIndicators = false;
      },
      error: (err) => {
        console.error(err);
        this.loadIndicators = false;
      },
    });
  }

  onPageChange(event: any) {
    this.pagination.pageNumber = event.page + 1;
    this.pagination.pageSize = event.rows;
    this.getAllpagamentos();
  }

  viewDetails(pagamento: any) {
    this.pagamentoID = pagamento?.apoio?.id;
    this.showDetalhesPagamento = true;
  }

  // controle de menus
  openedMenu: Menu | null = null;

  setOpenedMenu(menu: Menu) {
    if (this.openedMenu && this.openedMenu !== menu) {
      this.openedMenu.hide();
    }
    this.openedMenu = menu;
  }

  clearOpenedMenu() {
    this.openedMenu = null;
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.openedMenu) {
      this.openedMenu.hide();
      this.openedMenu = null;
    }
  }

  explorarCampanhas(): void {
    this.router.navigate(['/campanhas']);
  }
}
